import { useMemo } from 'react';
import { Order_By, type Guestrides_Order_By } from '@gql/base-types';
import { useGuestRideStore, type GuestRideSortBy } from '../stores/guestRideStore';
import { STATUS_FILTER_MAP } from '../constants/guestRideConstants';
import { getEffectiveDateRange } from '../utils/dateRangeUtils';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Build Hasura order_by object from sortBy + sortOrder */
function buildOrderBy(sortBy: GuestRideSortBy, sortOrder: 'asc' | 'desc'): Guestrides_Order_By[] {
  const dir = sortOrder === 'asc' ? Order_By.Asc : Order_By.Desc;
  // 'cost' sorts by the latest ridehail's actual_price
  if (sortBy === 'cost') {
    return [{ ridehails_aggregate: { max: { actual_price: dir } } }];
  }
  // sortBy is a guestrides column; the dynamic key can't be inferred to a
  // specific Guestrides_Order_By field, so assert the (runtime-valid) shape.
  return [{ [sortBy]: dir } as Guestrides_Order_By];
}

/** Build Hasura where clause for search across multiple fields */
function buildSearchCondition(search: string) {
  const term = `%${search}%`;
  return {
    _or: [
      { guest_name: { _ilike: term } },
      { guest_phone: { _ilike: term } },
      { pickup_address: { _ilike: term } },
      { dropoff_address: { _ilike: term } },
      { notes: { _ilike: term } },
    ],
  };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useGuestRideQueryVariables(customerId: number) {
  const { filters, page, pageSize, sortBy, sortOrder, viewMode } = useGuestRideStore();
  const { statusFilter, search, dateRange, startDate, endDate } = filters;

  return useMemo(() => {
    // Base condition: customer_id
    const conditions: any[] = [{ customer_id: { _eq: customerId } }];

    // Status filter
    if (statusFilter !== 'all') {
      const statuses = STATUS_FILTER_MAP[statusFilter];
      if (statuses && statuses.length > 0) {
        conditions.push({ status: { _in: statuses } });
      }
    }

    // Search
    if (search.trim()) {
      conditions.push(buildSearchCondition(search.trim()));
    }

    // Date range filter
    const effectiveRange = getEffectiveDateRange(dateRange, startDate, endDate);
    if (effectiveRange) {
      conditions.push({
        _or: [
          { created_at: { _gte: effectiveRange.start, _lte: effectiveRange.end } },
          { ridehails: { created_at: { _gte: effectiveRange.start, _lte: effectiveRange.end } } },
        ],
      });
    }

    const where = conditions.length === 1 ? conditions[0] : { _and: conditions };
    const order_by = buildOrderBy(sortBy, sortOrder);

    // Map mode: fetch all filtered results (no pagination)
    const isMapMode = viewMode === 'map';

    return {
      where,
      order_by,
      limit: isMapMode ? undefined : pageSize,
      offset: isMapMode ? undefined : page * pageSize,
    };
  }, [customerId, statusFilter, search, dateRange, startDate, endDate, sortBy, sortOrder, page, pageSize, viewMode]);
}
